import { useEffect, useMemo, useState } from 'react'
import {
  Box, CssBaseline, ThemeProvider, CircularProgress, Alert,
} from '@mui/material'
import theme from './theme/theme'
import { apiGetAll } from './requests/api'
import {
  STATUS_VALUES, SIDE_VALUES,
  getDisplayStatus, getEntrySides, getEntryOwners, getAgeDays, distinctValues,
} from './utils/aggregate'
import DashboardFilterBar from './components/DashboardFilterBar'
import KpiCards           from './components/KpiCards'
import DashboardCharts    from './components/charts/DashboardCharts'
import RiskTable          from './components/RiskTable'

const EMPTY_FILTERS = {
  vendor: '', team: '', type: '', side: '', tech: '', owner: '', status: '', period: 'all',
}

// Predicado + extractor de cada campo filtrável. Side e Owner são multi-valor.
const PRED = {
  vendor: (r, v) => r.vendor === v,
  team:   (r, v) => r.teamName === v,
  type:   (r, v) => r.feature === v,
  side:   (r, v) => getEntrySides(r).includes(v),
  tech:   (r, v) => r.technology === v,
  owner:  (r, v) => getEntryOwners(r).includes(v),
  status: (r, v) => getDisplayStatus(r) === v,
}
const KEYFN = {
  vendor: (r) => r.vendor,
  team:   (r) => r.teamName,
  type:   (r) => r.feature,
  side:   getEntrySides,
  tech:   (r) => r.technology,
  owner:  getEntryOwners,
  status: getDisplayStatus,
}
// Ordenações fixas para status e side; os restantes ficam alfabéticos.
const orderBy = (field, values) => {
  if (field === 'status') return STATUS_VALUES.filter((v) => values.includes(v))
  if (field === 'side')   return SIDE_VALUES.filter((v) => values.includes(v))
  return [...values].sort((a, b) => String(a).localeCompare(String(b)))
}

export default function App() {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [filters, setFilters] = useState(EMPTY_FILTERS)

  useEffect(() => {
    let alive = true
    apiGetAll()
      .then((res) => { if (alive) setEntries(Array.isArray(res.data) ? res.data : (res.data?.items ?? [])) })
      .catch((e)  => { if (alive) setError(e?.message || 'Failed to load activities') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  // Testa uma entry contra todos os filtros, podendo excluir um campo (usado
  // para o cascade: as opções de um filtro ignoram o próprio filtro).
  const matches = (r, exclude) => {
    for (const key of Object.keys(PRED)) {
      if (key === exclude) continue
      if (filters[key] && !PRED[key](r, filters[key])) return false
    }
    if (filters.period !== 'all' && getAgeDays(r) > Number(filters.period)) return false
    return true
  }

  // Linhas totalmente filtradas — alimentam counters, gráficos e tabela.
  const rows = useMemo(() => entries.filter((r) => matches(r, null)), [entries, filters])

  // Cascade: opções de cada dropdown = valores presentes quando se aplicam
  // todos os OUTROS filtros. Mantém sempre o valor já escolhido na lista.
  const options = useMemo(() => {
    const out = {}
    for (const field of Object.keys(PRED)) {
      const subset = entries.filter((r) => matches(r, field))
      const vals = distinctValues(subset, KEYFN[field])
      if (filters[field] && !vals.includes(filters[field])) vals.push(filters[field])
      out[field] = orderBy(field, vals)
    }
    return out
  }, [entries, filters])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: { xs: 1.5, md: 2.5 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Box sx={{ mb: 2 }}><KpiCards rows={rows} /></Box>
            <DashboardFilterBar filters={filters} setFilters={setFilters} options={options} onReset={() => setFilters(EMPTY_FILTERS)} />
            <DashboardCharts rows={rows} />
            <RiskTable rows={rows} />
          </>
        )}
      </Box>
    </ThemeProvider>
  )
}
